/**
 * ClinicContext - Active clinic context when provider enters via clinic QR
 *
 * Holds activeClinicId, activeClinicCode, and approvalStatus. Persisted to
 * sessionStorage so refresh keeps clinic context. Used by ChartMind to resolve
 * Position 3 from clinic config (clinic-only).
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'healthdesk_active_clinic';

const defaultState = {
  activeClinicId: null,
  activeClinicCode: null,
  approvalStatus: null, // 'allowed' | 'pending' | 'denied'
};

const ClinicContext = createContext(defaultState);

export function useClinicContext() {
  const ctx = useContext(ClinicContext);
  if (!ctx) {
    throw new Error('useClinicContext must be used within ClinicContextProvider');
  }
  return ctx;
}

function loadFromStorage() {
  try {
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    return {
      activeClinicId: parsed.activeClinicId ?? null,
      activeClinicCode: parsed.activeClinicCode ?? null,
      approvalStatus: parsed.approvalStatus ?? null,
    };
  } catch {
    return defaultState;
  }
}

function saveToStorage(state) {
  try {
    if (typeof sessionStorage !== 'undefined') {
      if (state.activeClinicId) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch (e) {
    console.warn('[ClinicContext] sessionStorage write failed:', e);
  }
}

export function ClinicContextProvider({ children }) {
  const [state, setState] = useState(loadFromStorage);

  useEffect(() => {
    setState(loadFromStorage());
  }, []);

  const setClinicContext = useCallback(({ activeClinicId, activeClinicCode, approvalStatus = 'allowed' }) => {
    const next = {
      activeClinicId: activeClinicId ?? null,
      activeClinicCode: activeClinicCode ?? null,
      approvalStatus: approvalStatus ?? null,
    };
    setState(next);
    saveToStorage(next);
  }, []);

  const clearClinicContext = useCallback(() => {
    setState(defaultState);
    saveToStorage(defaultState);
  }, []);

  const value = {
    ...state,
    setClinicContext,
    clearClinicContext,
  };

  return (
    <ClinicContext.Provider value={value}>
      {children}
    </ClinicContext.Provider>
  );
}

export default ClinicContext;
